<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('paymentTools.managePayments') }}</span>
      </div>
      <div class="ov-header__actions">
        <button type="button" class="ov-btn ov-btn--primary" @click="$router.push('/creerModePaiement')">
          <v-icon small>mdi-plus</v-icon>
          Nouveau moyen de paiement
        </button>
      </div>
    </div>

    <div class="ov-card">
      <div class="ov-card__head">
        <div class="ov-card__title">
          Moyens enregistrés
          <span class="ov-count">{{ paymentToolList.length }}</span>
        </div>
        <div class="ov-filters">
          <select
            v-if="$store.state.userAuthentified.roleCode == 'RoleAdminSocieteMere'"
            v-model="compagnieTransportRaisonSociale"
            class="ov-select"
            @change="getPaymentToolApi(compagnieTransportRaisonSociale)"
          >
            <option :value="null" disabled>{{ $t('paymentTools.selectCompany') }}</option>
            <option v-for="c in compagnieTransportList" :key="c.raisonSociale" :value="c.raisonSociale">
              {{ c.raisonSociale }}
            </option>
          </select>
          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input v-model="search" type="text" class="ov-input" :placeholder="$t('paymentTools.searchPlaceholder')" />
          </div>
          <div class="ov-toggle">
            <button
              type="button"
              class="ov-toggle__opt"
              :class="{ 'ov-toggle__opt--active': viewMode === 'table' }"
              title="Tableau"
              @click="viewMode = 'table'"
            >
              <v-icon small>mdi-format-list-bulleted</v-icon>
            </button>
            <button
              type="button"
              class="ov-toggle__opt"
              :class="{ 'ov-toggle__opt--active': viewMode === 'grid' }"
              title="Cartes"
              @click="viewMode = 'grid'"
            >
              <v-icon small>mdi-view-grid-outline</v-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- VUE TABLEAU -->
      <v-data-table
        v-if="viewMode === 'table'"
        :headers="headers"
        :items="paymentToolList"
        :loading="loading"
        :search="search"
        loader-height="2"
        class="ov-table"
        :loading-text="$t('paymentTools.loadingData')"
        :no-data-text="$t('paymentTools.noPaymentAvailable')"
      >
        <template #[`item.typeModePaiementDesignation`]="{ item }">
          <span class="ptl-type">
            <v-icon small :color="getPaymentColor(item.typeModePaiementDesignation)">
              {{ getPaymentIcon(item.typeModePaiementDesignation) }}
            </v-icon>
            {{ item.typeModePaiementDesignation }}
          </span>
        </template>
        <template #[`item.designation`]="{ item }">
          <span class="ptl-designation">{{ item.designation }}</span>
        </template>
        <template #[`item.telephoneGenerique`]="{ item }">
          <span v-if="item.telephoneGenerique">+225 {{ item.telephoneGenerique }}</span>
          <span v-else class="ptl-dash">—</span>
        </template>
        <template #[`item.actions`]="{ item }">
          <div class="ov-row-actions ptl-actions">
            <button
              type="button"
              class="ov-icon-btn"
              :title="$t('paymentTools.editMethod')"
              @click="activeModeUpdating(item)"
            >
              <v-icon small>mdi-pencil-outline</v-icon>
            </button>
          </div>
        </template>
      </v-data-table>

      <!-- VUE GRILLE -->
      <div v-else class="ov-card__body">
        <div v-if="loading" class="ptl-loading">
          <v-progress-circular indeterminate color="#159e72" size="36"></v-progress-circular>
        </div>
        <div v-else-if="filteredList.length === 0" class="ov-empty">
          {{ $t('paymentTools.noPaymentAvailable') }}
        </div>
        <div v-else class="ptl-grid">
          <div v-for="(item, i) in filteredList" :key="i" class="ptl-tile">
            <div class="ptl-tile__top">
              <span class="ptl-type">
                <v-icon small :color="getPaymentColor(item.typeModePaiementDesignation)">
                  {{ getPaymentIcon(item.typeModePaiementDesignation) }}
                </v-icon>
                {{ item.typeModePaiementDesignation }}
              </span>
              <button
                type="button"
                class="ov-icon-btn"
                :title="$t('paymentTools.editMethod')"
                @click="activeModeUpdating(item)"
              >
                <v-icon small>mdi-pencil-outline</v-icon>
              </button>
            </div>
            <div class="ptl-tile__name">{{ item.designation }}</div>
            <div class="ptl-tile__phone">
              <v-icon x-small>mdi-phone-outline</v-icon>
              <span v-if="item.telephoneGenerique">+225 {{ item.telephoneGenerique }}</span>
              <span v-else>—</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== PANNEAU DE MODIFICATION ===== -->
    <v-navigation-drawer v-model="editDrawerOpen" right fixed temporary width="420" class="ov-panel">
      <div class="ov-panel__head">
        <div>
          <div class="ov-card__kicker">{{ $t('paymentTools.editPaymentMethod') }}</div>
          <div class="ov-card__title">{{ updatePaymentTool.designation || '—' }}</div>
        </div>
        <button type="button" class="ov-icon-btn" title="Fermer" @click="closeDialogUpdatingPayment">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <div class="ov-panel__body">
        <div class="ov-field">
          <label class="ov-label">{{ $t('paymentTools.designation') }}<span class="ov-required">*</span></label>
          <input
            v-model="updatePaymentTool.designation"
            type="text"
            class="ov-input"
            :placeholder="$t('paymentTools.designation')"
          />
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('paymentTools.phoneLabel') }}<span class="ov-required">*</span></label>
          <div class="ov-input-group">
            <span class="ov-input-prefix">+225</span>
            <input
              v-model="updatePaymentTool.telephoneGenerique"
              type="text"
              class="ov-input"
              :placeholder="$t('paymentTools.accountPlaceholder')"
            />
          </div>
        </div>

        <div class="ov-field">
          <label class="ov-label">{{ $t('paymentTools.paymentTypeLabel') }}</label>
          <input v-model="updatePaymentTool.typeModePaiementDesignation" type="text" class="ov-input" disabled />
        </div>
      </div>

      <template #append>
        <div class="ov-panel__foot">
          <button type="button" class="ov-btn ov-btn--secondary" @click="closeDialogUpdatingPayment">
            <v-icon small>mdi-close</v-icon>
            {{ $t('paymentTools.cancel') }}
          </button>
          <button type="button" class="ov-btn ov-btn--primary" @click="apiUpdatePaymentTool">
            <v-icon small>mdi-content-save-outline</v-icon>
            {{ $t('paymentTools.save') }}
          </button>
        </div>
      </template>
    </v-navigation-drawer>

    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_CREER_MODE_PAIEMENT,
  API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
  API_OBTENIR_MODE_PAIEMENT_PAR_COMPAGNIE,
  HEADERS,
} from "@/components/globalConfig/globalConstConfig";
import {
  showErrorMessage,
  showSuccessMessage,
} from "@/modules/messages/messageProcess";

export default {
  name: "PaymentToolListView",
  components: {},
  data() {
    return {
      compagnieTransportList: [],
      compagnieTransportRaisonSociale: null,

      paymentToolList: [],

      paymentToolListObject: {
        data: {
          compagnieTransportRaisonSociale: null,
        },
      },

      editDrawerOpen: false,
      viewMode: 'table',

      options: {
        index: 0,
        size: 8,
      },

      overlay: false,
      successMsg: null,
      errorMsg: null,
      loading: true,
      search: "",

      updatePaymentTool: {
        id: null,
        designation: null,
        telephoneGenerique: null,
        typeModePaiementDesignation: null,
        compagnieTransportRaisonSociale: null,
      },

      paymentUpdatingDataToSend: {
        datas: [],
      },
    };
  },

  methods: {
    // Helper pour obtenir l'icône selon le type de paiement
    getPaymentIcon(type) {
      const typeMap = {
        'Mobile Money': 'mdi-cellphone',
        'Orange Money': 'mdi-cellphone',
        'MTN Money': 'mdi-cellphone',
        'Moov Money': 'mdi-cellphone',
        'Wave': 'mdi-cellphone',
        'Carte bancaire': 'mdi-credit-card',
        'Espèces': 'mdi-cash',
        'Virement': 'mdi-bank-transfer',
      };
      return typeMap[type] || 'mdi-wallet';
    },

    // Helper pour obtenir la couleur selon le type de paiement
    getPaymentColor(type) {
      const colorMap = {
        'Mobile Money': '#159e72',
        'Orange Money': '#FF6600',
        'MTN Money': '#FFCC00',
        'Moov Money': '#0066CC',
        'Wave': '#00D9A5',
        'Carte bancaire': '#0c2461',
        'Espèces': '#159e72',
        'Virement': '#0c2461',
      };
      return colorMap[type] || '#159e72';
    },

    // Récupérer la liste des moyens de paiement d'une compagnie.
    // Admin société mère : la compagnie choisie dans le filtre ; autres rôles : leur propre compagnie.
    async getPaymentToolApi(raisonSociale) {
      const compagnie = this.isAdminSocieteMere
        ? raisonSociale || this.compagnieTransportRaisonSociale
        : this.$store.state.userAuthentified.compagnieTransportRaisonSociale;

      if (!compagnie) {
        this.paymentToolList = [];
        this.loading = false;
        return;
      }

      this.loading = true;
      this.paymentToolListObject.data.compagnieTransportRaisonSociale = compagnie;
      await axios
        .post(
          API_OBTENIR_MODE_PAIEMENT_PAR_COMPAGNIE,
          this.paymentToolListObject,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code != 800) {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            } else {
              this.paymentToolList = response.data.items;
            }
          } else {
            this.errorMsg = this.$t('paymentTools.internalError');
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // OBTENIR LA LISTE DES DEMANDES D'ADHESIONS VALIDÉES
    async getCompagnieTransportList() {
      if (
        this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere"
      ) {
        axios
          .post(
            API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
            this.options,
            {
              headers: HEADERS(this.$store.state.userAuthentified.token),
            }
          )
          .then((response) => {
            if (response.status == 200) {
              if (response.data.status.code != 800) {
                this.errorMsg = response.data.status.message;
                showErrorMessage();
              } else {
                this.compagnieTransportList = response.data.items;
              }
            } else {
              this.errorMsg = this.$t('travelerCategory.dataError');
              showErrorMessage();
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          });
      }
    },

    // OPEN DIALOG FOR UPDATING BUS
    activeModeUpdating(mode) {
      this.editDrawerOpen = true;
      this.updatePaymentTool.id = mode.id;
      this.updatePaymentTool.designation = mode.designation;
      this.updatePaymentTool.telephoneGenerique = mode.telephoneGenerique;
      this.updatePaymentTool.typeModePaiementDesignation =
        mode.typeModePaiementDesignation;
      this.updatePaymentTool.compagnieTransportRaisonSociale =
        this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere"
          ? this.compagnieTransportRaisonSociale
          : this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
    },

    async apiUpdatePaymentTool() {
      const checkPaymentTool =
        this.updatePaymentTool.designation != null &&
        this.updatePaymentTool.designation !== "" &&
        this.updatePaymentTool.telephoneGenerique != null &&
        this.updatePaymentTool.telephoneGenerique !== "";

      if (!checkPaymentTool) {
        this.errorMsg = this.$t('paymentTools.requiredFieldsError');
        showErrorMessage();
      } else {
        this.paymentUpdatingDataToSend.datas.push(this.updatePaymentTool);
        this.overlay = true;
        await axios
          .put(API_CREER_MODE_PAIEMENT, this.paymentUpdatingDataToSend, {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          })
          .then((response) => {
            if (response.status == 200) {
              if (response.data.status.code == 800) {
                this.successMsg = response.data.status.message;
                showSuccessMessage();
                this.getPaymentToolApi(); // Recharger la liste
              } else {
                this.errorMsg = response.data.status.message;
                showErrorMessage();
              }
            } else {
              this.errorMsg = this.$t('paymentTools.creationError');
              showErrorMessage();
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          })
          .finally(() => {
            this.paymentUpdatingDataToSend.datas = [];
            this.closeDialogUpdatingPayment();
            this.overlay = false;
          });
      }
    },

    // FERMER LA BOITE DE DIALOG POUR LA MODIFICATION DE BUS
    closeDialogUpdatingPayment() {
      this.editDrawerOpen = false;
    },
  },

  computed: {
    isAdminSocieteMere() {
      return this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere";
    },

    headers() {
      return [
        { text: this.$t('paymentTools.paymentType'), value: 'typeModePaiementDesignation', align: 'start' },
        { text: this.$t('paymentTools.designation'), value: 'designation', align: 'start' },
        { text: this.$t('paymentTools.phoneNumber'), value: 'telephoneGenerique', align: 'start' },
        { text: this.$t('paymentTools.actions'), value: 'actions', sortable: false, align: 'center' },
      ];
    },
    filteredList() {
      if (!this.search) return this.paymentToolList;
      const s = this.search.toLowerCase();
      return this.paymentToolList.filter(
        (p) =>
          (p.designation || '').toLowerCase().includes(s) ||
          (p.typeModePaiementDesignation || '').toLowerCase().includes(s) ||
          (p.telephoneGenerique || '').includes(s)
      );
    },
  },

  mounted() {
    this.getPaymentToolApi();
    this.getCompagnieTransportList();
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/admin/ui";

.ptl-designation {
  font-weight: 700;
  color: $ov-navy;
}

.ptl-dash {
  color: $ov-subtle;
}

.ptl-type {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: $ov-ink;
}

.ptl-actions {
  justify-content: center;
}

.ptl-loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.ptl-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.ptl-tile {
  padding: 16px;
  border: 1px solid $ov-line;
  border-radius: 12px;
  background: #ffffff;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: rgba(21, 158, 114, 0.4);
  }
}

.ptl-tile__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
}

.ptl-tile__name {
  font-size: 15px;
  font-weight: 800;
  color: $ov-navy;
  margin-bottom: 6px;
  word-break: break-word;
}

.ptl-tile__phone {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: $ov-muted;

  .v-icon {
    color: $ov-subtle !important;
  }
}
</style>
